"use client";

import React, { useState } from 'react';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { InlineRichText } from '@/components/atoms/RichText';
import { ContactForm as ContactFormType } from '@/content/types';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { trackFormSubmissionGTM } from '@/lib/analytics';
import { getStoredUTMParams } from '@/lib/utm';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useSalesforceIdentity } from '@/components/consent/useSalesforceIdentity';
import { type ContactFormData, type FormErrors, validateContactForm, isFormValid } from '@/lib/forms/validation';
import { submitContactForm } from '@/lib/forms/contactFormService';
import { Box } from '@/components/atoms/Box';
import { Heading } from '@/components/atoms/Heading';
import { Text } from '@/components/atoms/Text';
import { RawButton } from '@/components/atoms/RawButton';
import { useHydrated } from '@/lib/hooks/useHydrated';
type FormState = 'idle' | 'submitting' | 'success' | 'error';
interface ContactFormProps extends ContactFormType {
  _key: string;
  // Legacy prop no longer used but passed from Sanity
  endpointUrl?: string;
}
export function ContactForm({
  heading,
  subheading,
  body,
  // endpointUrl is passed from Sanity but ignored in favor of the unified API
  submitButtonText = 'Submit',
  firstNameLabel = 'First Name',
  lastNameLabel = 'Last Name',
  emailLabel = 'Email Address',
  phoneLabel = 'Phone',
  messageLabel = 'Message',
  messagePlaceholder = 'How can we help you?',
  successHeading = 'Thank you!',
  successMessage = "Your message has been sent. We'll be in touch soon.",
  errorMessage = 'Something went wrong. Please try again or contact us directly.'
}: ContactFormProps) {
  const [formState, setFormState] = useState<FormState>('idle');
  const [errors, setErrors] = useState<FormErrors>({});
  const [serverError, setServerError] = useState<string>('');
  const isHydrated = useHydrated();
  const {
    executeRecaptcha
  } = useGoogleReCaptcha();
  const {
    sendIdentityEvent
  } = useSalesforceIdentity();
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const data: ContactFormData = {
      firstname: formData.get('firstname') as string,
      lastname: formData.get('lastname') as string,
      email: formData.get('email') as string,
      company: formData.get('company') as string,
      phone: formData.get('phone') as string,
      note: formData.get('note') as string
    };
    const fieldErrors = validateContactForm(data, {
      firstNameLabel,
      lastNameLabel,
      emailLabel
    });
    setErrors(fieldErrors);
    if (!isFormValid(fieldErrors)) {
      return;
    }
    setFormState('submitting');
    setServerError('');
    const utmParams = getStoredUTMParams();
    try {
      let recaptchaToken: string | undefined;
      if (executeRecaptcha) {
        try {
          recaptchaToken = await executeRecaptcha('contact_form');
        } catch (recaptchaError) {
          console.warn('reCAPTCHA token generation failed:', recaptchaError);
        }
      }
      const result = await submitContactForm({
        data,
        recaptchaToken,
        pageUrl: window.location.href,
        utmParams: utmParams as unknown as Record<string, string>
      });
      if (result.success) {
        setFormState('success');
        if (data.email) {
          sendIdentityEvent(data.email, 'contact_form_submit', {
            firstName: data.firstname,
            lastName: data.lastname
          });
        }
        trackFormSubmissionGTM('contact_form', true, utmParams as unknown as Record<string, string>);
      } else {
        setServerError(result.message || errorMessage);
        setFormState('error');
        trackFormSubmissionGTM('contact_form', false, utmParams as unknown as Record<string, string>);
      }
    } catch {
      setServerError(errorMessage || 'An unexpected error occurred.');
      setFormState('error');
      trackFormSubmissionGTM('contact_form', false, utmParams as unknown as Record<string, string>);
    }
  };
  const handleRetry = () => {
    setFormState('idle');
    setServerError('');
  };
  return <Box className="w-full">
      {formState === 'success' ? <Box className="text-center py-12">
          <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-6" />
          <Heading level={2} className="text-2xl md:text-3xl font-bold mb-4">{successHeading}</Heading>
          <Text as="p" className="text-lg opacity-90">{successMessage}</Text>
        </Box> : <Box>
          {(heading || subheading || body) && <Box className="mb-8">
              {heading && <Heading level={2} className="text-2xl md:text-3xl font-bold mb-3">{heading}</Heading>}
              {subheading && <Text as="p" className="text-lg opacity-90 mb-4">{subheading}</Text>}
              {body && <Box className="prose prose-semantic max-w-none">
                  <InlineRichText content={body} />
                </Box>}
            </Box>}

          {formState === 'error' && serverError && <Box className="mb-6 p-4 bg-destructive/10 border border-destructive/50 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
              <Box className="flex-1">
                <Text as="p" className="text-destructive">{serverError}</Text>
                <RawButton onClick={handleRetry} className="text-sm text-destructive underline hover:opacity-80 mt-2">
                  Try again
                </RawButton>
              </Box>
            </Box>}

          <form onSubmit={handleSubmit} aria-label="Contact form" role="form" className="space-y-5" data-hydrated={isHydrated ? "true" : undefined}>
            <Box className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Box>
                <label htmlFor="firstname" className="block text-sm font-medium mb-2">
                  {firstNameLabel} <Text className="text-primary">*</Text>
                </label>
                <Input id="firstname" name="firstname" type="text" autoComplete="given-name" aria-label={firstNameLabel} className="h-12 bg-muted/20 border-border placeholder:text-muted-foreground focus:border-ring" aria-invalid={!!errors.firstname} />
                {errors.firstname && <Text as="p" className="text-destructive text-sm mt-1">{errors.firstname}</Text>}
              </Box>
              <Box>
                <label htmlFor="lastname" className="block text-sm font-medium mb-2">
                  {lastNameLabel} <Text className="text-primary">*</Text>
                </label>
                <Input id="lastname" name="lastname" type="text" autoComplete="family-name" aria-label={lastNameLabel} className="h-12 bg-muted/20 border-border placeholder:text-muted-foreground focus:border-ring" aria-invalid={!!errors.lastname} />
                {errors.lastname && <Text as="p" className="text-destructive text-sm mt-1">{errors.lastname}</Text>}
              </Box>
            </Box>

            <Box className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Box>
                <label htmlFor="company" className="block text-sm font-medium mb-2">
                  Company Name <Text className="text-primary">*</Text>
                </label>
                <Input id="company" name="company" type="text" autoComplete="organization" aria-label="Company Name" className="h-12 bg-muted/20 border-border placeholder:text-muted-foreground focus:border-ring" aria-invalid={!!errors.company} />
                {errors.company && <Text as="p" className="text-destructive text-sm mt-1">{errors.company}</Text>}
              </Box>
              <Box>
                <label htmlFor="phone" className="block text-sm font-medium mb-2">
                  {phoneLabel} <Text className="text-muted-foreground font-normal text-xs ml-1">(optional)</Text>
                </label>
                <Input id="phone" name="phone" type="tel" autoComplete="tel" aria-label={phoneLabel} className="h-12 bg-muted/20 border-border placeholder:text-muted-foreground focus:border-ring" aria-invalid={!!errors.phone} />
                {errors.phone && <Text as="p" className="text-destructive text-sm mt-1">{errors.phone}</Text>}
              </Box>
            </Box>

            <Box>
              <label htmlFor="email" className="block text-sm font-medium mb-2">
                {emailLabel} <Text className="text-primary">*</Text>
              </label>
              <Input id="email" name="email" type="email" autoComplete="email" aria-label={emailLabel} className="h-12 bg-muted/20 border-border placeholder:text-muted-foreground focus:border-ring" aria-invalid={!!errors.email} />
              {errors.email && <Text as="p" className="text-destructive text-sm mt-1">{errors.email}</Text>}
            </Box>

            <Box>
              <label htmlFor="note" className="block text-sm font-medium mb-2">
                {messageLabel}
              </label>
              <textarea id="note" name="note" rows={4} placeholder={messagePlaceholder || undefined} aria-label={messageLabel} className="w-full rounded-md bg-muted/20 border border-border placeholder:text-muted-foreground px-3 py-3 focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring transition-colors" />
            </Box>

            <Button type="submit" size="lg" disabled={formState === 'submitting'} aria-label={submitButtonText} className="w-full md:w-auto h-12 px-10 bg-primary hover:bg-primary/90 text-primary-foreground font-bold">
              {formState === 'submitting' ? <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending...
                </> : submitButtonText}
            </Button>
          </form>
        </Box>}
    </Box>;
}